"use client";

import { useCallback, useEffect, useState, type RefObject } from "react";

export type ImageFrameRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/** Pixel rect of the visible image inside a container (object-fit: contain). */
export function useObjectContainImageFrame(
  containerRef: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
): ImageFrameRect | null {
  const [frame, setFrame] = useState<ImageFrameRect | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      setFrame(null);
      return;
    }

    const img =
      container.querySelector<HTMLImageElement>("img[data-file-stage-image]") ??
      container.querySelector<HTMLImageElement>("img");
    if (!img || img.naturalWidth <= 0 || img.naturalHeight <= 0) {
      setFrame(null);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const cw = img.clientWidth;
    const ch = img.clientHeight;
    if (cw <= 0 || ch <= 0) {
      setFrame(null);
      return;
    }

    const imageAspect = img.naturalWidth / img.naturalHeight;
    const boxAspect = cw / ch;

    let renderedWidth: number;
    let renderedHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (imageAspect > boxAspect) {
      renderedWidth = cw;
      renderedHeight = cw / imageAspect;
      offsetX = 0;
      offsetY = (ch - renderedHeight) / 2;
    } else {
      renderedHeight = ch;
      renderedWidth = ch * imageAspect;
      offsetX = (cw - renderedWidth) / 2;
      offsetY = 0;
    }

    const imgOffsetLeft = imgRect.left - containerRect.left;
    const imgOffsetTop = imgRect.top - containerRect.top;

    const next: ImageFrameRect = {
      left: imgOffsetLeft + offsetX,
      top: imgOffsetTop + offsetY,
      width: renderedWidth,
      height: renderedHeight,
    };

    setFrame((prev) =>
      prev &&
      prev.left === next.left &&
      prev.top === next.top &&
      prev.width === next.width &&
      prev.height === next.height
        ? prev
        : next,
    );
  }, [containerRef]);

  useEffect(() => {
    measure();

    const container = containerRef.current;
    if (!container) return;

    const img = container.querySelector("img");
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    if (img) {
      ro.observe(img);
      img.addEventListener("load", measure);
    }

    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      img?.removeEventListener("load", measure);
      window.removeEventListener("resize", measure);
    };
  }, [measure, containerRef, ...deps]);

  return frame;
}
